/**
 * Durable local chunk queue (W4-04/05, browser edition).
 * Every chunk is written to IndexedDB *before* upload is attempted, and removed only
 * after the server acknowledges the object. Survives reloads and offline periods.
 */
export type ChunkState = "recorded" | "queued" | "uploading" | "uploaded" | "failed";

export interface QueuedChunk {
  key: string; // `${meetingId}:${seq}`
  meetingId: string;
  seq: number;
  blob: Blob;
  mime: string;
  offsetSec: number;
  durationSec: number;
  state: ChunkState;
  attempts: number;
  createdAt: number;
}

const DB = "timo-capture";
const STORE = "chunks";

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => {
      const store = req.result.createObjectStore(STORE, { keyPath: "key" });
      store.createIndex("meeting", "meetingId");
      store.createIndex("state", "state");
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T> | void): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(STORE, mode);
        const req = fn(t.objectStore(STORE));
        t.oncomplete = () => resolve((req as IDBRequest<T> | undefined)?.result as T);
        t.onerror = () => reject(t.error);
      }),
  );
}

export const queue = {
  put: (c: QueuedChunk) => tx("readwrite", (s) => void s.put(c)),
  get: (key: string) => tx<QueuedChunk | undefined>("readonly", (s) => s.get(key)),
  remove: (key: string) => tx("readwrite", (s) => void s.delete(key)),
  async pending(meetingId?: string): Promise<QueuedChunk[]> {
    const all = await tx<QueuedChunk[]>("readonly", (s) => s.getAll());
    return all
      .filter((c) => c.state !== "uploaded" && (!meetingId || c.meetingId === meetingId))
      .sort((a, b) => a.seq - b.seq);
  },
  async setState(key: string, state: ChunkState, attempts?: number) {
    const c = await queue.get(key);
    if (!c) return;
    c.state = state;
    if (attempts !== undefined) c.attempts = attempts;
    await queue.put(c);
  },
};
